import { api } from "@/modules/api";
import type { components } from "@/__openapi__/backend/schema";

export interface InviteSummary {
  sharedItemId: string;
  sharedItemKind: components["schemas"]["SharedItemInviteSharedItemDetailsSchema"]["shared_item_kind"];
  displayTitle: string | null;
  inviterDisplayName: string | null;
  inviterEmail: string | null;
  inviterPhotoUrl: string | null;
  previewHtml: string | null;
}

export type InviteLookupResult =
  | { kind: "success"; invite: InviteSummary }
  | { kind: "expired"; message?: string }
  | { kind: "already-used"; message?: string }
  | { kind: "not-found"; message?: string }
  | { kind: "error"; message: string };

export type AcceptInviteResult =
  | { kind: "accepted"; message?: string }
  | { kind: "expired"; message?: string }
  | { kind: "already-used"; message?: string }
  | { kind: "not-found"; message?: string }
  | { kind: "error"; message: string };

type VerifySharedItemInviteResponse = components["schemas"]["VerifySharedItemInviteResponse"];
type AcceptSharedItemInviteResponse = components["schemas"]["AcceptSharedItemInviteResponse"];

const extractErrorDetail = (error: unknown): string | undefined => {
  if (!error || typeof error !== "object") {
    return undefined;
  }

  if ("detail" in error && typeof (error as { detail?: string }).detail === "string") {
    return (error as { detail?: string }).detail;
  }

  return undefined;
};

const mapSharedItemDetails = (
  response: VerifySharedItemInviteResponse | AcceptSharedItemInviteResponse
): InviteSummary | null => {
  const details = response.shared_item_details;
  const invite = response.invite;

  if (!details) {
    return null;
  }

  return {
    sharedItemId: invite.shared_item_id,
    sharedItemKind: details.shared_item_kind ?? invite.shared_item_kind,
    displayTitle: details.display_title ?? null,
    inviterDisplayName: details.inviter_profile_display_name ?? null,
    inviterEmail: details.inviter_profile_email_address ?? null,
    inviterPhotoUrl: details.inviter_profile_photo_url ?? null,
    previewHtml: details.preview_html ?? null,
  };
};

export const fetchInviteByToken = async (
  inviteId: string | undefined,
  token: string | null
): Promise<InviteLookupResult> => {
  const normalizedInviteId = inviteId?.trim();
  const normalizedToken = token?.trim();

  if (!normalizedInviteId || !normalizedToken) {
    return { kind: "not-found" };
  }

  try {
    const response = await api.post("/v2/shared-item-invites/{invite_id}/verify", {
      params: { path: { invite_id: normalizedInviteId } },
      body: { verification_token: normalizedToken },
    });

    if (!response.data || response.error) {
      const status = response.response?.status ?? 0;
      const detail = extractErrorDetail(response.error);

      if (status === 404 || detail === "shared_item_missing") {
        return { kind: "not-found", message: detail ?? undefined };
      }

      if (detail === "The inviter no longer has permission to share this item.") {
        return { kind: "error", message: detail };
      }

      if (detail === "We couldn't find this shared item. It may have been removed.") {
        return { kind: "not-found", message: detail };
      }

      if (detail === "Invite has expired.") {
        return { kind: "expired", message: detail };
      }

      if (
        detail === "Invite has been revoked." ||
        detail === "Space account cannot access this invite."
      ) {
        return { kind: "not-found", message: detail };
      }

      if (
        detail === "Verification token does not match invite." ||
        detail === "Verification token mismatch."
      ) {
        return { kind: "not-found", message: detail };
      }

      if (detail) {
        return { kind: "error", message: detail };
      }

      return { kind: "error", message: "We couldn't verify this invitation." };
    }

    const data = response.data;

    if (data.is_revoked || data.invite.is_revoked) {
      return { kind: "not-found" };
    }

    if (data.is_expired) {
      return { kind: "expired" };
    }

    if (data.invite.status?.toUpperCase() === "ACCEPTED") {
      return { kind: "already-used" };
    }

    const inviteSummary = mapSharedItemDetails(data);

    if (!inviteSummary) {
      return { kind: "not-found" };
    }

    return { kind: "success", invite: inviteSummary };
  } catch (_error) {
    return { kind: "error", message: "We couldn't verify this invitation." };
  }
};

export const acceptInviteByToken = async (
  inviteId: string | undefined,
  token: string | null
): Promise<AcceptInviteResult> => {
  const normalizedInviteId = inviteId?.trim();
  const normalizedToken = token?.trim();

  if (!normalizedInviteId || !normalizedToken) {
    return { kind: "not-found" };
  }

  try {
    const response = await api.post("/v2/shared-item-invites/{invite_id}/accept", {
      params: { path: { invite_id: normalizedInviteId } },
      body: { verification_token: normalizedToken },
    });

    if (!response.data || response.error) {
      const status = response.response?.status ?? 0;
      const detail = extractErrorDetail(response.error);

      if (status === 404 || detail === "shared_item_missing") {
        return { kind: "not-found", message: detail ?? undefined };
      }

      if (detail === "Invite has expired.") {
        return { kind: "expired", message: detail };
      }

      if (detail === "Invite has been revoked.") {
        return { kind: "not-found", message: detail };
      }

      if (detail === "Space account cannot access this invite.") {
        return { kind: "not-found", message: detail };
      }

      if (
        detail === "Verification token mismatch." ||
        detail === "Verification token does not match invite."
      ) {
        return { kind: "not-found", message: detail };
      }

      if (detail === "Invite has already been accepted.") {
        return { kind: "already-used", message: detail };
      }

      if (detail === "The inviter no longer has permission to share this item.") {
        return { kind: "error", message: detail };
      }

      if (detail === "We couldn't find this shared item. It may have been removed.") {
        return { kind: "not-found", message: detail };
      }

      if (
        detail ===
        "We couldn't add you to this item. It may already be shared or is no longer available."
      ) {
        return { kind: "error", message: detail };
      }

      if (status === 401) {
        return { kind: "error", message: "Please sign in to accept this invitation." };
      }

      return { kind: "error", message: detail ?? "Something went wrong. Please try again." };
    }

    const data = response.data;

    if (data.invite.status?.toUpperCase() === "ACCEPTED") {
      return { kind: "accepted" };
    }

    return { kind: "accepted" };
  } catch (error) {
    return {
      kind: "error",
      message: (error as Error).message ?? "Something went wrong. Please try again.",
    };
  }
};
